import { useState } from "react";
import { Plus } from "lucide-react";
import {
  TRANSFER_BASES,
  useMoneyAccounts,
  useRecordTransfer,
  useSetTransferStatus,
  useTransfers,
  useVoidTransfer,
  type Transfer,
} from "@/api/cash";
import { ReasonForm } from "@/components/forms/ReasonForm";
import { TransferForm } from "@/components/forms/TransferForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField, TextField } from "@/components/ui/field";
import { formatDate, formatMoney } from "@/lib/format";

type Panel = { kind: "record" } | { kind: "void"; transfer: Transfer } | null;

function basisLabel(basis: Transfer["basis"]): string {
  return TRANSFER_BASES.find((b) => b.value === basis)?.label ?? basis;
}

function received(transfer: Transfer): string {
  return formatMoney(transfer.received_amount, transfer.received_currency);
}

export function TransfersPanel() {
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [accountId, setAccountId] = useState("");
  const [includeVoided, setIncludeVoided] = useState(false);
  const [panel, setPanel] = useState<Panel>(null);
  const accounts = useMoneyAccounts(false);
  const everyAccount = useMoneyAccounts(true);
  const transfers = useTransfers({
    since: since || undefined,
    until: until || undefined,
    accountId: accountId || undefined,
    includeVoided,
  });
  const record = useRecordTransfer();
  const complete = useSetTransferStatus();
  const voidTransfer = useVoidTransfer();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        {panel === null && (
          <Button onClick={() => setPanel({ kind: "record" })} disabled={!accounts.data}>
            <Plus className="h-4 w-4" aria-hidden />
            Record transfer
          </Button>
        )}
      </div>
      {panel?.kind === "record" && accounts.data && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Record a transfer</CardTitle>
          </CardHeader>
          <CardContent>
            <TransferForm
              accounts={accounts.data}
              onSubmit={async (input) => {
                await record.mutateAsync(input);
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
            />
          </CardContent>
        </Card>
      )}
      {panel?.kind === "void" && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>
              Void {panel.transfer.from_account_name} → {panel.transfer.to_account_name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ReasonForm
              label="Why is it being voided?"
              hint="The transfer stays on record, marked voided, and stops moving balances."
              submitLabel="Void transfer"
              submitVariant="destructive"
              onSubmit={async (reason) => {
                await voidTransfer.mutateAsync({
                  id: panel.transfer.id,
                  reason,
                  version: panel.transfer.version,
                });
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
              onReload={() => {
                void transfers.refetch();
                setPanel(null);
              }}
            />
          </CardContent>
        </Card>
      )}
      <div className="grid gap-3 sm:grid-cols-[auto_auto_minmax(0,14rem)_1fr] sm:items-end sm:gap-4">
        <TextField
          id="transfers-since"
          label="From"
          type="date"
          value={since}
          onChange={setSince}
        />
        <TextField id="transfers-until" label="To" type="date" value={until} onChange={setUntil} />
        <SelectField
          id="transfers-account"
          label="Account"
          value={accountId}
          onChange={setAccountId}
          placeholder="All accounts"
          options={(everyAccount.data ?? []).map((a) => ({
            value: a.id,
            label: a.archived_at ? `${a.name} (archived)` : a.name,
          }))}
        />
        <Checkbox
          id="transfers-voided"
          label="Show voided transfers"
          checked={includeVoided}
          onChange={setIncludeVoided}
          className="sm:pb-2"
        />
      </div>
      <RequestError error={complete.error} />
      {transfers.isPending ? (
        <p className="text-muted-foreground">Loading transfers…</p>
      ) : transfers.isError ? (
        <RequestError error={transfers.error} />
      ) : transfers.data.length === 0 ? (
        <EmptyState
          title="No transfers here"
          description="Money moved between the company's accounts, the sister company and the directors."
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3 md:hidden">
            {transfers.data.map((t) => (
              <li
                key={t.id}
                className="flex flex-col gap-1 rounded-lg border bg-card p-4 text-sm shadow-sm"
              >
                <p className="font-semibold">
                  {formatMoney(t.amount, t.currency)} → {received(t)}
                </p>
                <p>
                  {t.from_account_name} → {t.to_account_name}{" "}
                  <Badge
                    variant={
                      t.voided_at ? "destructive" : t.status === "pending" ? "warning" : "success"
                    }
                  >
                    {t.voided_at ? "Voided" : t.status}
                  </Badge>
                </p>
                <p className="text-xs text-muted-foreground">
                  {[formatDate(t.made_on), basisLabel(t.basis), t.reference]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {!t.voided_at && panel === null && (
                  <div className="flex gap-2">
                    {t.status === "pending" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={complete.isPending}
                        onClick={() =>
                          complete.mutate({ id: t.id, status: "completed", version: t.version })
                        }
                      >
                        Mark received
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setPanel({ kind: "void", transfer: t })}
                    >
                      Void
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          <div className="hidden table-frame md:block">
            <table className="data-table">
              <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">From → to</th>
                  <th className="px-4 py-2 text-right font-medium">Sent</th>
                  <th className="px-4 py-2 text-right font-medium">Received</th>
                  <th className="px-4 py-2 font-medium">Why</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {transfers.data.map((t) => (
                  <tr key={t.id} className="border-b last:border-0">
                    <td className="whitespace-nowrap px-4 py-2">{formatDate(t.made_on)}</td>
                    <td className="px-4 py-2">
                      {t.from_account_name} → {t.to_account_name}
                      <span className="block text-xs text-muted-foreground">
                        {[t.reference, t.void_reason].filter(Boolean).join(" · ")}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                      {formatMoney(t.amount, t.currency)}
                      {Number(t.deductions) > 0 && (
                        <span className="block text-xs text-muted-foreground">
                          − {formatMoney(t.deductions, t.currency)}
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                      {received(t)}
                    </td>
                    <td className="px-4 py-2">{basisLabel(t.basis)}</td>
                    <td className="px-4 py-2">
                      <Badge
                        variant={
                          t.voided_at
                            ? "destructive"
                            : t.status === "pending"
                              ? "warning"
                              : "success"
                        }
                      >
                        {t.voided_at ? "Voided" : t.status}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right">
                      {!t.voided_at && panel === null && (
                        <>
                          {t.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={complete.isPending}
                              onClick={() =>
                                complete.mutate({
                                  id: t.id,
                                  status: "completed",
                                  version: t.version,
                                })
                              }
                            >
                              Mark received
                            </Button>
                          )}{" "}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPanel({ kind: "void", transfer: t })}
                          >
                            Void
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
